import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, loadTranslations, t } from "../shared/utils"

export default class extends Controller {
  static targets = ["content", "headerName", "breadcrumbName"]
  static values = { profileId: String, profilesPath: String, announcementBasePath: String, clubBasePath: String }

  connect() {
    loadTranslations()
    this._fetchProfile()
  }

  async _fetchProfile() {
    const data = await apiGet(`/api/v1/profiles/${this.profileIdValue}`)
    if (!data) return

    const profile = data.data || data
    if (this.hasHeaderNameTarget) this.headerNameTarget.textContent = profile.full_name
    if (this.hasBreadcrumbNameTarget) this.breadcrumbNameTarget.textContent = profile.full_name

    this.contentTarget.innerHTML = this._renderProfile(profile)
  }

  _renderProfile(p) {
    const name = escapeHtml(p.full_name)

    const avatarHtml = p.avatar_url
      ? `<img src="${escapeHtml(p.avatar_url)}" alt="${name}" class="rounded-circle" style="width: 80px; height: 80px; object-fit: cover;">`
      : `<div class="d-inline-flex align-items-center justify-content-center rounded-circle bg-light" style="width: 80px; height: 80px;">
           <i class="fa-solid fa-2x ${p.sex === 1 ? "fa-mars text-primary" : "fa-venus text-danger"}"></i>
         </div>`

    let locationHtml = ""
    if (p.city) {
      let loc = escapeHtml(p.city)
      if (p.voivodeship) loc += `, ${t(`voivodeships.${p.voivodeship}`)}`
      locationHtml = `<p class="text-muted mb-2"><i class="fa-solid fa-location-dot me-1"></i>${loc}</p>`
    }

    const ageHtml = p.age ? `<p class="text-muted mb-2">${p.age} ${t("dancer_profile.years_old")}</p>` : ""
    const heightHtml = p.height_cm ? `<p class="text-muted mb-2"><i class="fa-solid fa-ruler-vertical me-1"></i>${p.height_cm} cm</p>` : ""

    let ageCategoryHtml = ""
    if (p.federation_metadata?.age_category) {
      ageCategoryHtml = `<p class="text-muted mb-2"><i class="fa-solid fa-users me-1"></i>${escapeHtml(p.federation_metadata.age_category)}</p>`
    }

    let classesHtml = ""
    if (p.dance_classes && (p.dance_classes.st || p.dance_classes.la)) {
      classesHtml = `<div class="d-flex justify-content-center gap-2 mb-2">`
      if (p.dance_classes.st) classesHtml += `<span class="badge bg-dm-navy">ST: ${escapeHtml(p.dance_classes.st)}</span>`
      if (p.dance_classes.la) classesHtml += `<span class="badge bg-dm-navy">LA: ${escapeHtml(p.dance_classes.la)}</span>`
      classesHtml += `</div>`
    }

    const bioHtml = p.bio ? `<div class="mt-3 text-start"><p>${escapeHtml(p.bio)}</p></div>` : ""

    let partnerBadge = ""
    if (!p.has_partner) {
      if (p.has_announcement && p.active_announcement_id) {
        partnerBadge = `<div class="mt-2"><a href="${this.announcementBasePathValue}/${p.active_announcement_id}" class="badge bg-warning text-dark text-decoration-none"><i class="fa-solid fa-magnifying-glass me-1"></i>${t("show.looking_for_partner")}</a></div>`
      } else {
        partnerBadge = `<div class="mt-2"><span class="badge bg-warning text-dark"><i class="fa-solid fa-magnifying-glass me-1"></i>${t("show.looking_for_partner")}</span></div>`
      }
    }

    const profileCard = `
      <div class="card shadow-sm mb-4">
        <div class="card-body text-center py-4">
          <div class="mb-3">${avatarHtml}</div>
          <h4 class="mb-1">${name}</h4>
          ${locationHtml}${ageHtml}${heightHtml}${ageCategoryHtml}${classesHtml}${bioHtml}${partnerBadge}
        </div>
      </div>`

    let skillsCard = ""
    if (p.dance_skills?.length) {
      const skillItems = p.dance_skills.map(s => {
        const level = s.level ? `<span class="text-muted small ms-1">(${escapeHtml(s.level)})</span>` : ""
        return `<div class="col-sm-6"><div class="d-flex align-items-center gap-2 p-2 bg-light rounded"><i class="fa-solid fa-circle-check text-success"></i><div><span class="fw-medium">${escapeHtml(s.name)}</span>${level}</div></div></div>`
      }).join("")
      skillsCard = `
        <div class="card shadow-sm mb-4">
          <div class="card-body">
            <h5 class="card-title mb-3"><i class="fa-solid fa-music me-2"></i>${t("dancer_profile.dance_styles")}</h5>
            <div class="row g-2">${skillItems}</div>
          </div>
        </div>`
    }

    let affiliationsCard = ""
    if (p.memberships?.length) {
      const memberItems = p.memberships.map(m => {
        const clubLink = m.has_subscription
          ? `<a href="${this.clubBasePathValue}/${escapeHtml(m.club_slug)}" class="text-decoration-none"><span class="badge bg-success">${escapeHtml(m.club_name)}</span></a>`
          : `<span class="text-muted small">${escapeHtml(m.club_name)}</span>`
        const feds = (m.federations || []).map(f => `<span class="badge bg-secondary">${escapeHtml(f.name)} (${escapeHtml(f.shortcut)})</span>`).join(" ")
        return `<div class="d-flex align-items-center gap-2 flex-wrap mb-3">${clubLink} ${feds}</div>`
      }).join("")
      affiliationsCard = `
        <div class="card shadow-sm mb-4">
          <div class="card-body">
            <h5 class="card-title mb-3"><i class="fa-solid fa-building me-2"></i>${t("dancer_profile.affiliations")}</h5>
            ${memberItems}
          </div>
        </div>`
    }

    let announcementAlert = ""
    if (p.has_announcement && p.active_announcement_id) {
      const link = `<a href="${this.announcementBasePathValue}/${p.active_announcement_id}" class="alert-link fw-medium">${t("show.active_announcement_link")}</a>`
      announcementAlert = `
        <div class="alert alert-info border-0 shadow-sm p-3 mb-4 text-center">
          <i class="fa-solid fa-bullhorn me-2"></i>${t("show.has_announcement_html").replace("%{link}", link)}
        </div>`
    }

    const backBtn = `
      <div class="text-center">
        <a href="${this.profilesPathValue}" class="default-btn">
          <span><i class="fa-solid fa-arrow-left me-2"></i>${t("show.back_to_profiles")}</span>
        </a>
      </div>`

    return `
      <div class="row justify-content-center">
        <div class="col-lg-8 col-12">
          ${profileCard}${skillsCard}${affiliationsCard}${announcementAlert}${backBtn}
        </div>
      </div>`
  }
}
